import { MatrixAnimation } from "./main";

export type MatrixPreset = "cyberpunk" | "ocean-blue" | "fire-red" | "monochrome" | "purple-haze" | "neon-pink" | "matrix" | "digital-rain";


const r = (a: number, b: number): string =>
    Array.from({ length: b - a + 1 }, (_, i) => String.fromCodePoint(a + i)).join("");


export const MATRIX_PRESETS: Record<
    MatrixPreset,
    ConstructorParameters<typeof MatrixAnimation>[1]
    > = {
    cyberpunk: {
        fadeStrength: 0.04,
        rainWidth: 10,
        rainHeight: 18,
        rainDrop: {
            headColor: "rgba(255,45,120,0.95)",
            trailColors: [
                "rgba(255,45,120,0.3)",
                "rgba(160,32,240,0.2)",
                "rgba(255,0,255,0.1)",
            ],
            randomizeScale: true,
            randomizePosition: true,
            charArrays: [
                r(0xFF01, 0xFF5E), // fullwidth Latin  — glitched corp display text
                r(0xFF65, 0xFF9F), // half-width Katakana — neon sign kanji
                r(0x2500, 0x257F), // box drawing       — hardwired circuit traces
                r(0x2190, 0x21AF), // arrows            — data flow indicators
                r(0x2100, 0x214F), // letterlike        — hacked glyphs ℃℉™℗ℤℝℵ
            ],
            fontSize: 16,
            fontFamily: "monospace",
            alignToColumns: true,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 4,
            maxMoveSpeed: 12,
            correlateScaleSpeed: false,
            minFrameDelay: 30,
            maxFrameDelay: 50,
            randomizeFrameDelay: true,
        },
        rainGenerator: { density: 1 },
    },
    "ocean-blue": {
        fadeStrength: 0.05,
        minFrameTime: 50,
        rainWidth: 10,
        rainHeight: 18,
        rainDrop: {
            headColor: "rgba(0,245,212,0.95)",
            trailColors: [
                "rgba(0,245,212,0.3)",
                "rgba(0,187,249,0.2)",
                "rgba(0,100,200,0.1)",
            ],
            charArrays: "🮤🮥🮦🮧🮨🮩🮪🮫🮬🮭🮮",
            randomizeScale: true,
            randomizePosition: true,
            fontSize: 14,
            fontFamily: "monospace",
            alignToColumns: true,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 4,
            maxMoveSpeed: 12,
            correlateScaleSpeed: false,
            minFrameDelay: 30,
            maxFrameDelay: 50,
            randomizeFrameDelay: true,
        },
        rainGenerator: { density: .65 },
    },
    "fire-red": {
        fadeStrength: 0.04,
        rainWidth: 10,
        rainHeight: 24,
        rainDrop: {
            headColor: "rgba(255,200,100,0.95)",
            trailColors: [
                "rgba(255,100,50,0.3)",
                "rgba(200,50,0,0.2)",
                "rgba(100,0,0,0.1)",
            ],
            randomizeScale: true,
            randomizePosition: true,
            charArrays: "🯰🯱🯲🯳🯴🯵🯶🯷🯸🯹",
            fontSize: 16,
            fontFamily: "monospace",
            alignToColumns: true,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 16,
            maxMoveSpeed: 24,
            correlateScaleSpeed: false,
            minFrameDelay: 50,
            maxFrameDelay: 100,
            randomizeFrameDelay: true,
        },
        rainGenerator: { density: 1 },
    },
    monochrome: {
        fadeStrength: 0.05,
        rainWidth: 10,
        rainHeight: 18,
        rainDrop: {
            headColor: "rgba(255,255,255,0.95)",
            trailColors: [
                "rgba(200,200,200,0.3)",
                "rgba(150,150,150,0.2)",
                "rgba(100,100,100,0.1)",
            ],
            charArrays: "⸪⸫⸬⸭⁖⁘⁙⁚⁛",
            randomizeScale: true,
            randomizePosition: true,
            fontSize: 14,
            fontFamily: "monospace",
            alignToColumns: true,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 4,
            maxMoveSpeed: 12,
            correlateScaleSpeed: false,
            minFrameDelay: 30,
            maxFrameDelay: 50,
            randomizeFrameDelay: true,
        },
        rainGenerator: { density: .75 },
    },
    "purple-haze": {
        rainWidth: 10,
        rainHeight: 18,
        minFrameTime: 75,
        rainGenerator: { density: 0.5 },
        rainDrop: {
            direction: "TD",
            charArrays: [
                "ｦｧｨｩｪｫｬｭｮｯｰｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ─━│┃┄┅┆┇┈┉┊┋┌┍┎┏┐┑┒┓└┕┖┗┘┙┚┛├┝┞┟┠┡┢┣┤┥┦┧┨┩┪┫┬┭┮┯┰┱┲┳┴┵┶┷┸┹┺┻┼┽┾┿∀∁∂∃∄∅∆∇∈∉∊∋∌∍∎∏∐∑−∓∔∕∖∗∘∙√∛∜∝∞∟∠∡∢∣∤∥∦∧∨∩∪∫∬∭∮∯▀▁▂▃▄▅▆▇█▉▊▋▌▍▎▏▐░▒▓▔▕▖▗▘▙▚▛▜▝▞▟"
            ],
            headColor: "rgba(255,255,255,0.8)",
            trailColor: "rgba(140,62,225,1.00)",
            fontSize: 16,
            fontFamily: "monospace",
            randomizeScale: false,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 4,
            maxMoveSpeed: 12,
            correlateScaleSpeed: false,
            minFrameDelay: 30,
            maxFrameDelay: 50,
            randomizePosition: true,
            randomizeFrameDelay: true,
            jitterLeftStrength: 0,
            jitterRightStrength: 0,
            jitterUpStrength: 0,
            jitterDownStrength: 0,
        },
        trailBloomSize: 0,
        trailBloomColor: "#ffffff",
        headBloomSize: 0,
        headBloomColor: "#ffffff",
        warmupIterations: 50,
        fadeStrength: 0.05,
    },
    "neon-pink": {
        fadeStrength: 0.04,
        rainWidth: 10,
        rainHeight: 18,
        rainDrop: {
            headColor: "rgba(255,100,200,0.95)",
            trailColors: [
                "rgba(255,150,220,0.3)",
                "rgba(255,50,150,0.2)",
                "rgba(200,0,100,0.1)",
            ],
            randomizeScale: true,
            randomizePosition: true,
            charArrays: [
                "⠁⠂⠃⠄⠅⠆⠇⠈⠉⠊⠋⠌⠍⠎⠏⠐⠑⠒⠓⠔⠕⠖⠗⠘⠙⠚⠛⠜⠝⠞⠟⠠⠡⠢⠣⠤⠥⠦⠧⠨⠩⠪⠫⠬⠭⠮⠯⠰⠱⠲⠳⠴⠵⠶⠷⠸⠹⠺⠻⠼⠽⠾⠿⡀⡁⡂⡃⡄⡅⡆⡇⡈⡉⡊⡋⡌⡍⡎⡏⡐⡑⡒⡓⡔⡕⡖⡗⡘⡙⡚⡛⡜⡝⡞⡟⡠⡡⡢⡣⡤⡥⡦⡧⡨⡩⡪⡫⡬⡭⡮⡯⡰⡱⡲⡳⡴⡵⡶⡷⡸⡹⡺⡻⡼⡽⡾⡿⢀⢁⢂⢃⢄⢅⢆⢇⢈⢉⢊⢋⢌⢍⢎⢏⢐⢑⢒⢓⢔⢕⢖⢗⢘⢙⢚⢛⢜⢝⢞⢟⢠⢡⢢⢣⢤⢥⢦⢧⢨⢩⢪⢫⢬⢭⢮⢯⢰⢱⢲⢳⢴⢵⢶⢷⢸⢹⢺⢻⢼⢽⢾⢿⣀⣁⣂⣃⣄⣅⣆⣇⣈⣉⣊⣋⣌⣍⣎⣏⣐⣑⣒⣓⣔⣕⣖⣗⣘⣙⣚⣛⣜⣝⣞⣟⣠⣡⣢⣣⣤⣥⣦⣧⣨⣩⣪⣫⣬⣭⣮⣯⣰⣱⣲⣳⣴⣵⣶⣷⣸⣹⣺⣻⣼⣽⣾⣿",
                "⠁⠂⠃⠄⠅⠆⠇"
            ],
            fontSize: 16,
            fontFamily: "monospace",
            alignToColumns: true,
            correlateScaleSpeed: true,
            minMoveSpeed: 4,
            maxMoveSpeed: 12,
            minFontSize: 12,
            maxFontSize: 22,
            minFrameDelay: 50,
            frameDelay: 50,
        },
        rainGenerator: { density: 1.5 },
    },
    matrix: {
        rainWidth: 10,
        rainHeight: 18,
        minFrameTime: 125,
        syncFrame: false,
        rainGenerator: { density: 0.5 },
        rainDrop: {
            direction: "TD",
            charArrays: ["0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZアァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン"],
            headColor: "rgba(255,255,255,0.8)",
            trailColor: "rgba(62, 225, 78, 1.00)",
            fontSize: 16,
            fontFamily: "monospace",
            randomizePosition: true,
            frameDelay: 50,
            minFrameDelay: 50,
            maxFrameDelay: 130,
            randomizeFrameDelay: true,
            randomizeScale: true,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 8,
            maxMoveSpeed: 42,
            correlateScaleSpeed: true,
            jitterLeftStrength: 0,
            jitterRightStrength: 0,
            jitterUpStrength: 0,
            jitterDownStrength: 0,
        },
        trailBloomSize: 8,
        trailBloomColor: "#82ffa9",
        headBloomSize: 4,
        headBloomColor: "#ffffff",
        warmupIterations: 50,
        fadeStrength: 0.05,
    },
    "digital-rain": {
        rainWidth: 10,
        rainHeight: 18,
        minFrameTime: 75,
        rainGenerator: { density: 0.5 },
        rainDrop: {
            direction: "TD",
            charArrays: [
                "⠁⠂⠃⠄⠅⠆⠇⠈⠉⠊⠋⠌⠍⠎⠏⠐⠑⠒⠓⠔⠕⠖⠗⠘⠙⠚⠛⠜⠝⠞⠟⠠⠡⠢⠣⠤⠥⠦⠧⠨⠩⠪⠫⠬⠭⠮⠯⠰⠱⠲⠳⠴⠵⠶⠷⠸⠹⠺⠻⠼⠽⠾⠿⡀⡁⡂⡃⡄⡅⡆⡇⡈⡉⡊⡋⡌⡍⡎⡏⡐⡑⡒⡓⡔⡕⡖⡗⡘⡙⡚⡛⡜⡝⡞⡟⡠⡡⡢⡣⡤⡥⡦⡧⡨⡩⡪⡫⡬⡭⡮⡯⡰⡱⡲⡳⡴⡵⡶⡷⡸⡹⡺⡻⡼⡽⡾⡿⢀⢁⢂⢃⢄⢅⢆⢇⢈⢉⢊⢋⢌⢍⢎⢏⢐⢑⢒⢓⢔⢕⢖⢗⢘⢙⢚⢛⢜⢝⢞⢟⢠⢡⢢⢣⢤⢥⢦⢧⢨⢩⢪⢫⢬⢭⢮⢯⢰⢱⢲⢳⢴⢵⢶⢷⢸⢹⢺⢻⢼⢽⢾⢿⣀⣁⣂⣃⣄⣅⣆⣇⣈⣉⣊⣋⣌⣍⣎⣏⣐⣑⣒⣓⣔⣕⣖⣗⣘⣙⣚⣛⣜⣝⣞⣟⣠⣡⣢⣣⣤⣥⣦⣧⣨⣩⣪⣫⣬⣭⣮⣯⣰⣱⣲⣳⣴⣵⣶⣷⣸⣹⣺⣻⣼⣽⣾⣿",
                "⠁⠂⠃⠄⠅⠆⠇",
                "⣾⣽⣼⣻⣺⣹⣟⢿⢾⣯⣮⣭⡿⡾⡽⢵⡷⡟⠿⡯⡷⡿"
            ],
            headColor: "rgba(255,255,255,0.8)",
            trailColor: "rgba(140,62,225,1.00)",
            fontSize: 16,
            fontFamily: "monospace",
            randomizeScale: false,
            minFontSize: 12,
            maxFontSize: 22,
            moveSpeed: 0,
            minMoveSpeed: 4,
            maxMoveSpeed: 12,
            correlateScaleSpeed: false,
            minFrameDelay: 30,
            maxFrameDelay: 50,
            randomizePosition: true,
            randomizeFrameDelay: true,
        },
        trailBloomSize: 0,
        trailBloomColor: "#ffffff",
        headBloomSize: 0,
        headBloomColor: "#ffffff",
        warmupIterations: 50,
        fadeStrength: 0.05,
    },
};